// deps
import React, { memo, useMemo } from "react";

// internal-deps
import { format, isCurrentSystemThemeDark } from "@swiggy-private/core-ui";
import { getCloudinaryUrl, isEmpty, TEAM_LINE } from "@swiggy-private/js-utils";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// types
import { IEventStatusBanner } from "@dineout-payment-success-ui/interfaces";

// styles
import {
    StatusText,
    StatusBannerWrapper,
    TextWrapper,
    StyledImage,
    QRText,
    TicketTextWrapper,
    QRImageContainer,
    QRImageWrapper,
    CornerAccents,
    PartialPaymentButton,
    PartialPaymentButtonText,
} from "./EventBookingStatusBanner.styles";
import { BgContainer } from "@dineout-payment-success-ui/components/dineout-payment-success/styles";

// constants
import {
    BASE64_IMG_PREFIX,
    BOOKING_ID_TEXT,
    BOOKING_STATUS,
    EVENT_BOOKING_STATUS_DETAILS as STATUS_DETAILS,
    SacramentoFont,
} from "@dineout-payment-success-ui/constants";

// helpers
import { getBookingStatusHelperText, getColorCode } from "@dineout-payment-success-ui/helpers/index";

const EventBookingStatusBanner: React.FC<IEventStatusBanner> = ({
    bookingStatus,
    bookingId,
    qrCode,
    bookingStatusBannerSubtitle,
    partialPaymentsConfig,
}) => {
    const isPartialPayment = partialPaymentsConfig?.isPartialPayment || false;
    const payCta = partialPaymentsConfig?.payCta;
    const onPaymentCtaClick = payCta?.onPaymentCtaClick;

    const isPendingWithPartialPayment = useMemo(
        () => bookingStatus === BOOKING_STATUS.PENDING && isPartialPayment,
        [bookingStatus, isPartialPayment],
    );

    // Use RESERVED status when pending with partial payment
    const effectiveStatus = useMemo(() => {
        return isPendingWithPartialPayment ? BOOKING_STATUS.RESERVED : bookingStatus;
    }, [isPendingWithPartialPayment, bookingStatus]);

    const showStatusDescription = useMemo(() => effectiveStatus === BOOKING_STATUS.PENDING, [effectiveStatus]);

    const bgImageUrl = useMemo(
        () =>
            getCloudinaryUrl(
                STATUS_DETAILS?.[effectiveStatus].BACKGROUND?.[isCurrentSystemThemeDark() ? "dark" : "light"] || "",
                {},
                { teamLine: TEAM_LINE.DINEOUT },
            ),
        [effectiveStatus],
    );

    const bookingStatusHelperText = getBookingStatusHelperText(true, effectiveStatus);
    const qrCodeUrl = qrCode ? BASE64_IMG_PREFIX + qrCode : "";

    const titleText = useMemo(() => {
        return format(STATUS_DETAILS?.[effectiveStatus]?.TEXT, bookingStatusHelperText);
    }, [effectiveStatus, bookingStatusHelperText]);

    return (
        <StatusBannerWrapper
            showStatusDescription={showStatusDescription}
            hasSubscription={false}
            isEventReservation
            isPartialPayment={isPartialPayment}
        >
            <SacramentoFont />
            <BgContainer>
                <StyledImage src={bgImageUrl} />
            </BgContainer>
            <TextWrapper>
                <StatusText colorKey={getColorCode(effectiveStatus)}>{titleText}</StatusText>
                <TicketTextWrapper>
                    {!isEmpty(qrCode) && (
                        <QRImageContainer>
                            <CornerAccents>
                                <span />
                            </CornerAccents>
                            <QRImageWrapper>
                                <Image src={qrCodeUrl} />
                            </QRImageWrapper>
                        </QRImageContainer>
                    )}
                    <Text variant="V2.body3.neutral.bold" color="text_Low_Emphasis">
                        {format(BOOKING_ID_TEXT, bookingId || "")}
                    </Text>
                    {!isEmpty(bookingStatusBannerSubtitle) && (
                        <QRText
                            variant="V2.body3.neutral.regular"
                            textColor={bookingStatusBannerSubtitle?.textColor}
                            colorKey="highlight"
                        >
                            {bookingStatusBannerSubtitle?.text}
                        </QRText>
                    )}
                    {isPartialPayment && payCta?.isApplicable && (
                        <PartialPaymentButton buttonType="tertiary" buttonSize="small" onClick={onPaymentCtaClick}>
                            <PartialPaymentButtonText variant="V2.body1.neutral.bold">
                                {payCta.text}
                            </PartialPaymentButtonText>
                        </PartialPaymentButton>
                    )}
                </TicketTextWrapper>
            </TextWrapper>
        </StatusBannerWrapper>
    );
};

export default memo(EventBookingStatusBanner);
